"use client";

import React, { ErrorInfo, ReactNode } from "react";

import { logException } from "@punchup/sentry";

interface Props {
  /** Names the wrapped feature in the Sentry report. */
  name: string;
  children: ReactNode;
  /** What to render instead of the feature once it throws. Defaults to nothing. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Contains the blast radius of one feature. An uncaught render error in it
 * would otherwise bubble to the nearest boundary (for a root-layout feature,
 * the root, blanking EVERY page). This boundary reports the error to Sentry and
 * renders `fallback`, so only that feature is lost.
 */
export default class FeatureErrorBoundary extends React.Component<
  Props,
  State
> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logException(error, {
      data: `[FeatureErrorBoundary:${this.props.name}] ${errorInfo.componentStack ?? ""}`,
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
